import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ACCESS_KEY, PatientAccountTicketRequestStatus, SpecialistProfileStatus} from '@/constants';
import {
  LoginOtpRequestPayload,
  OtpResponse,
  PatientSignupOtpRequestPayload,
  OtpResendRequestPayload,
  MfaResendRequestPayload,
  OtpVerifyRequestPayload,
  MfaVerifyRequestPayload,
  MfaResponse,
  GetProfileSuccessResponse,
  GetSpecialistProfileSuccessResponse,
  UpdateSpecialistProfileSuccessResponse,
  AuthSuccessResponse,
  OTPVerifySuccessReponse,
  AuthSliceInitalState,
  APIResponse,
  ProfileOtpRequestPayload,
  ProfileOtpResendPayload,
  ProfileOtpVerifyPayload,
  ProfileOTPBlockedResponse,
  ProfileOtpInvalidResponse,
  PatientSettingExportRequestPayload,
  PatientAccountDeletionRequestPayload,
  PatientAccountDeletionSuccessResponse,
  PatientSettingExportSuccessResponse,
  ValidateSpecialistInvitationRequestPayload,
  ValidateSpecialistInvitationSuccessResponse,
  ProfileOtpVerifySuccessResponse,
  GetAccountTicketStatusSuccessResponse,
} from '@/interface';
import {decryptInline} from '@/utils/encryption';
import {formatName} from '@/utils/common_functions';

const initialState: AuthSliceInitalState = {
  isLoading: false,
  profileupdateLoading: false,
  phoneVerifyLoading: false,
  emailVerifyLoading: false,
  isAccountDeletionLoading: false,
  isAccountExportLoading: false,

  authSuccess: false,
  authFailure: false,
  authData: null,
  isAuthenticated: false,
  otp_attempts: 0,
  otpTimer: '',
  persist: {
    canChangeEmail: false,
    canChangePhone: false,
    email_otp_timer: '',
    phone_otp_timer: '',
    email_otp_attempts: 0,
    phone_otp_attempts: 0,
  },
  authSession: {
    auth_type: null,
    email: '',
    phone: '',
    originalPhone: '',

    formData: {
      name: '',
      specialization: null,
      education: '',
      designation: '',
      experience: null,
      license: '',
      license_number: '',
      license_year: '',
      charge: null,
      location: -1,
    },

    disabled: false,
  },
  accountDeletionStatus: 0,
  accountExportStatus: 0,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * login otp
     */
    loginOtpRequest(state, action: PayloadAction<LoginOtpRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;

      // set the email in auth session
      state.authSession.email = action.payload.email_id;
    },
    loginOtpSuccess(state, action: PayloadAction<OtpResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.otp_attempts;
      state.otpTimer = new Date().toISOString();

      state.authSession.auth_type = 'login';
    },
    loginOtpFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      state.otp_attempts = 0;

      // reset the auth session type
      state.authSession.auth_type = null;
    },

    /**
     * signup otp
     */
    signupOtpRequest(state, action: PayloadAction<PatientSignupOtpRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;

      state.authSession.email = action.payload.email_id;
      state.authSession.phone = action.payload.phone;
      state.authSession.originalPhone = action.payload.phone;

      // Store name and location in formData for navigation state when going back
      if (!state.authSession.formData) {
        state.authSession.formData = {};
      }
      if (action.payload.name) {
        state.authSession.formData.name = action.payload.name;
      }
      if (action.payload.location !== undefined && action.payload.location !== -1) {
        state.authSession.formData.location = action.payload.location;
      }
    },
    signupOtpSuccess(state, action: PayloadAction<OtpResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.otp_attempts;
      state.otpTimer = new Date().toISOString();

      state.authSession.auth_type = 'signup';
    },
    signupOtpFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      state.otp_attempts = 0;

      state.authSession.auth_type = null;
    },

    /**
     * login OTP resend
     */
    loginOtpResendRequest(state, action: PayloadAction<OtpResendRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    loginOtpResendSuccess(state, action: PayloadAction<OtpResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.otp_attempts;
      state.otpTimer = new Date().toISOString();
    },
    loginOtpResendFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * sign up otp resend
     */
    signupOtpResendRequest(state, action: PayloadAction<OtpResendRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    signupOtpResendSuccess(state, action: PayloadAction<OtpResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.otp_attempts;
      state.otpTimer = new Date().toISOString();
    },
    signupOtpResendFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * login OTP verify
     */
    loginOTPVerifyRequest(state, action: PayloadAction<OtpVerifyRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    loginOTPVerifyFailure(state, action: PayloadAction<OtpResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      // if invalid otp or expired otp only then update otp attempts
      if (['E-10009', 'E-10012'].includes(action.payload.status_code)) {
        state.otp_attempts = action.payload.data.otp_attempts;
      }
      // disabled otp button
      if (action.payload.status_code === 'E-10011') {
        state.authSession.disabled = true;
      }
    },
    loginOTPVerifySuccess(state, action: PayloadAction<OTPVerifySuccessReponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.mfa_attempts;
      state.authSession.phone = action.payload.data.phone;
      state.otpTimer = new Date().toISOString();
    },

    /**
     * sign up otp verify
     */
    singupOTPVerifyRequest(state, action: PayloadAction<OtpVerifyRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    singupOTPVerifyFailure(state, action: PayloadAction<OtpResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      // if invalid otp or expired otp only then update otp attempts
      if (['E-10009', 'E-10012'].includes(action.payload.status_code)) {
        state.otp_attempts = action.payload.data?.otp_attempts;
      }

      // disabled otp button
      if (action.payload.status_code === 'E-10011') {
        state.authSession.disabled = true;
      }
    },
    singupOTPVerifySuccess(state, action: PayloadAction<OTPVerifySuccessReponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.mfa_attempts;
      state.authSession.phone = action.payload.data.phone;
      state.otpTimer = new Date().toISOString();
    },

    /**
     * login MFA verify
     */
    loginMFAVerifyRequest(state, action: PayloadAction<MfaVerifyRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    loginMFAVerifyFailure(state, action: PayloadAction<MfaResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      // if invalid otp or expired otp only then update otp attempts
      if (['E-10032', 'E-10033'].includes(action.payload.status_code)) {
        state.otp_attempts = action.payload.data.mfa_attempts;
      }
      // disabled otp button
      if (action.payload.status_code === 'E-10011') {
        state.authSession.disabled = true;
      }
    },
    loginMFAVerifySuccess(state, action: PayloadAction<AuthSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;

      // reset attempts timer
      state.otp_attempts = 3;
      state.otpTimer = new Date().toISOString();

      // set auth detailes
      state.isAuthenticated = true;
      state.authData = action.payload.data;

      // Store account_ticket_status from the response
      if (action.payload.data.account_ticket_status !== undefined) {
        state.accountDeletionStatus = action.payload.data.account_ticket_status;
      }

      localStorage.setItem(ACCESS_KEY, action.payload.access_token);
    },

    /**
     * signp MFA verify
     */
    signupMFAVerifyRequest(state, action: PayloadAction<MfaVerifyRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    signupMFAVerifyFailure(state, action: PayloadAction<MfaResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      // if invalid otp or expired otp only then update otp attempts
      if (['E-10032', 'E-10033'].includes(action.payload.status_code)) {
        state.otp_attempts = action.payload.data.mfa_attempts;
      }
      // disabled otp button
      if (action.payload.status_code === 'E-10011') {
        state.authSession.disabled = true;
      }
    },
    signupMFAVerifySuccess(state, action: PayloadAction<AuthSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;

      // reset attempts timer
      state.otp_attempts = 3;
      state.otpTimer = new Date().toISOString();

      // set auth detailes
      state.isAuthenticated = true;
      state.authData = action.payload.data;

      localStorage.setItem(ACCESS_KEY, action.payload.access_token);
    },

    /**
     * login MFA resend
     */
    loginMFAResendRequest(state, action: PayloadAction<MfaResendRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    loginMFAResendSuccess(state, action: PayloadAction<MfaResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.mfa_attempts;
      state.otpTimer = new Date().toISOString();
    },
    loginMFAResendFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * signp MFA resend
     */
    signupMFAResendRequest(state, action: PayloadAction<MfaResendRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    signupMFAResendSuccess(state, action: PayloadAction<MfaResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.otp_attempts = action.payload.data.mfa_attempts;
      state.otpTimer = new Date().toISOString();
    },
    signupMFAResendFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * specialist signup otp request
     */
    specialistSignupOtpRequest(state, action: PayloadAction<any>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    specialistSignupOtpSuccess(state, action: PayloadAction<{response_date: OtpResponse; request_payload: any}>) {
      state.isLoading = false;
      state.authSuccess = action.payload.response_date.status_code;
      state.otp_attempts = action.payload.response_date.data?.otp_attempts;
      state.otpTimer = new Date().toISOString();

      state.authSession.auth_type = 'specialist_signup';
      // set the form data after successful api call
      state.authSession.email = action.payload.request_payload.get('email_id');
      state.authSession.phone = action.payload.request_payload.get('phone');
      state.authSession.originalPhone = action.payload.request_payload.get('phone');

      const licenceFile: File = action.payload.request_payload.get('license');
      state.authSession.formData = {
        name: action.payload.request_payload.get('name') || '',
        specialization: action.payload.request_payload.get('specialization'),
        experience: action.payload.request_payload.get('experience'),
        designation: action.payload.request_payload.get('designation'),
        education: action.payload.request_payload.get('education'),
        license_number: action.payload.request_payload.get('license_number') || '',
        license_year: action.payload.request_payload.get('license_year') || '',
        license: licenceFile.name,
        charge: action.payload.request_payload.get('charge'),
        location: action.payload.request_payload.get('location'),
      };
    },
    specialistSignupOtpFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * verify licence
     */
    verifyLicenceRequest(state, action: PayloadAction<{license_number: string; license_year: string; email: string}>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    verifyLicenceSuccess(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
    },
    verifyLicenceFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * profile api
     */
    getProfileRequest(state) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    getProfileSuccess(state, action: PayloadAction<GetProfileSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      if (state.authData) {
        state.authData.photo_url = action.payload.data.photo;
        state.authData.username = action.payload.data.name;
        state.authData.email_id = action.payload.data.email_id;
        state.authData.phone = action.payload.data.phone;
        state.authData.address = action.payload.data.address;
        state.authData.dob = action.payload.data.dob;
        state.authData.gender = action.payload.data.gender;
        state.authData.height = action.payload.data.height;
        state.authData.weight = action.payload.data.weight;
        state.authData.medical_history = action.payload.data.medical_history;
        state.persist.canChangeEmail = action.payload.data.can_edit_email;
        state.persist.canChangePhone = action.payload.data.can_edit_phone;

        state.accountDeletionStatus = action.payload.data.account_delete_status;
        state.accountExportStatus = action.payload.data.account_export_status;
      }
    },

    getProfileFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * edit profile
     */
    editProfileRequest(state, action: PayloadAction<any>) {
      // since it accepts form data
      state.profileupdateLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    editProfileSuccess(state, action: PayloadAction<GetProfileSuccessResponse>) {
      state.profileupdateLoading = false;
      state.authSuccess = action.payload.status_code;
      if (state.authData) {
        state.authData.photo_url = action.payload.data.photo;
        state.authData.username = action.payload.data.name;
        state.authData.email_id = action.payload.data.email_id;
        state.authData.phone = action.payload.data.phone;
        state.authData.address = action.payload.data.address;
        state.authData.dob = action.payload.data.dob;
        state.authData.gender = action.payload.data.gender;
        state.authData.height = action.payload.data.height;
        state.authData.weight = action.payload.data.weight;
        state.authData.medical_history = action.payload.data.medical_history;
      }
    },

    editProfileFailure(state, action: PayloadAction<APIResponse>) {
      state.profileupdateLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * profile otp request
     */
    profileOtpRequestRequest(state, action: PayloadAction<ProfileOtpRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    profileOtpRequestFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    profileOtpRequestSuccess(state, action: PayloadAction<ProfileOtpInvalidResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      if (action.payload.data?.type === 'email') {
        state.persist.email_otp_timer = new Date().toISOString();
        state.persist.email_otp_attempts = action.payload.data.otp_attempts;
      }
      if (action.payload.data?.type === 'phone') {
        state.persist.phone_otp_timer = new Date().toISOString();
        state.persist.phone_otp_attempts = action.payload.data.otp_attempts;
      }
    },

    /**
     * profile otp resend
     */
    profileOtpResendRequest(state, action: PayloadAction<ProfileOtpResendPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    profileOtpResendFailure(state, action: PayloadAction<ProfileOTPBlockedResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10069') {
        if (action.payload.data?.type === 'email') {
          state.persist.canChangeEmail = false;
        }
        if (action.payload.data?.type === 'phone') {
          state.persist.canChangePhone = false;
        }
      }
    },

    profileOtpResendSuccess(state, action: PayloadAction<ProfileOtpInvalidResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      if (action.payload.data?.type === 'email') {
        state.persist.email_otp_timer = new Date().toISOString();
        state.persist.email_otp_attempts = action.payload.data?.otp_attempts;
      }
      if (action.payload.data?.type === 'phone') {
        state.persist.phone_otp_timer = new Date().toISOString();
        state.persist.phone_otp_attempts = action.payload.data?.otp_attempts;
      }
    },

    /**
     * profile otp verify
     */
    profileOtpVerifyRequest(state, action: PayloadAction<ProfileOtpVerifyPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    profileOtpVerifyFailure(state, action: PayloadAction<ProfileOtpInvalidResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;

      if (action.payload.data?.type === 'email') {
        if (action.payload.status_code === 'E-10069') {
          state.persist.canChangeEmail = false;
        }
        state.persist.email_otp_attempts = action.payload.data.otp_attempts;
      }

      if (action.payload.data?.type === 'phone') {
        if (action.payload.status_code === 'E-10069') {
          state.persist.canChangePhone = false;
        }
        state.persist.phone_otp_attempts = action.payload.data.otp_attempts;
      }
    },

    profileOtpVerifySuccess(state, action: PayloadAction<ProfileOtpVerifySuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      if (action.payload.status_code === 'S-10024' && action.payload.data.type === 'phone' && action.payload.data.phone && state.authData) {
        state.authData.phone = action.payload.data.phone || '';
      }
      if (action.payload.status_code === 'S-10030' && action.payload.data.type === 'email' && action.payload.data.email && state.authData) {
        state.authData.email_id = action.payload.data.email || '';
      }
    },

    /**
     * email or phone otp request
     */
    profileUpdateOtpRequest(state) {
      state.isLoading = true;
      state.authFailure = false;
    },
    profileUpdateOtpFailure(state) {},
    profileUpdateOtpSuccess(state) {},

    /**
     * get specialist profile api
     */
    getSpecialistProfileRequest(state) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    getSpecialistProfileSuccess(state, action: PayloadAction<GetSpecialistProfileSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      if (state.authData) {
        const responseData = action.payload.data;

        // Common fields
        state.authData.photo_url = responseData.photo;
        state.authData.username = responseData.name;
        // API returns email_id
        state.authData.email_id = responseData.email_id || '';
        state.authData.phone = responseData.phone;

        // Specialist-specific fields - store in authData as extended properties
        // Handle location - can be null, object with id/label, or just id
        if (responseData.location) {
          (state.authData as any).location =
            typeof responseData.location === 'object' ? responseData.location.id || responseData.location : responseData.location;
          (state.authData as any).location_label = typeof responseData.location === 'object' ? responseData.location.label : undefined;
        } else {
          (state.authData as any).location = null;
          (state.authData as any).location_label = null;
        }

        (state.authData as any).designation = responseData.designation;
        (state.authData as any).education = responseData.education;
        (state.authData as any).is_hospital_specialist = responseData.is_hospital_specialist;
        (state.authData as any).hospital_name = responseData.hospital_name;
        (state.authData as any).hospital_address = responseData.hospital_address;

        // Handle specialization - API returns object with id (as string) and label
        if (responseData.specialization) {
          const specId =
            typeof responseData.specialization === 'object'
              ? responseData.specialization.id
                ? Number(responseData.specialization.id)
                : null
              : typeof responseData.specialization === 'string'
              ? Number(responseData.specialization)
              : responseData.specialization;
          (state.authData as any).specialization = specId;
          (state.authData as any).specialization_label = typeof responseData.specialization === 'object' ? responseData.specialization.label : undefined;
        } else {
          (state.authData as any).specialization = null;
          (state.authData as any).specialization_label = null;
        }

        // API returns 'experience'
        (state.authData as any).years_of_experience = responseData.experience;

        // Handle license - API returns 'liscence_number' (typo in API) and 'url'
        (state.authData as any).license = responseData.license;
        (state.authData as any).license_number = responseData.license?.liscence_number;
        (state.authData as any).license_file = responseData.license?.url;

        // Signature - API may return specialist_signature or signature
        const responseAny = responseData as any;
        (state.authData as any).specialist_signature = responseAny.specialist_signature ?? responseAny.signature ?? null;
        (state.authData as any).signature = (state.authData as any).specialist_signature;

        (state.authData as any).professional_experience = responseData.professional_experience;

        // API returns 'qualifications'
        (state.authData as any).qualification = responseData.qualifications;
        (state.authData as any).qualifications = responseData.qualifications;

        // Handle awards - API has typo: 'awards_and_acheivements'
        const awardsData = responseData.awards_and_achievements || [];
        (state.authData as any).awards_and_achievements = Array.isArray(awardsData) ? JSON.stringify(awardsData) : awardsData;
        (state.authData as any).awards_achievements = Array.isArray(awardsData) ? JSON.stringify(awardsData) : awardsData;

        // Handle areas_of_expertise - may be double-encoded JSON strings
        let areasData = responseData.areas_of_expertise || [];
        if (Array.isArray(areasData) && areasData.length > 0) {
          // Check if first item is a JSON string that needs parsing
          const firstItem = areasData[0];
          if (typeof firstItem === 'string' && firstItem.startsWith('[')) {
            try {
              const parsed = JSON.parse(firstItem);
              if (Array.isArray(parsed)) {
                // If parsed is an array, use it; otherwise keep original
                areasData = parsed;
              }
            } catch {
              // If parsing fails, use as is
            }
          }
        }
        (state.authData as any).areas_of_expertise = Array.isArray(areasData) ? JSON.stringify(areasData) : areasData;

        (state.authData as any).profile_status = responseData.profile_status;

        // Set canChangeEmail and canChangePhone from API response
        state.persist.canChangeEmail = responseData.can_edit_email ?? false;
        state.persist.canChangePhone = responseData.can_edit_phone ?? false;
      }
    },

    getSpecialistProfileFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * update specialist profile
     */
    updateSpecialistProfileRequest(state, action: PayloadAction<FormData>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    updateSpecialistProfileSuccess(state, action: PayloadAction<UpdateSpecialistProfileSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      // Update profile_status in authData
      if (state.authData) {
        (state.authData as any).profile_status = action.payload.data.profile_status;
      }
    },

    updateSpecialistProfileFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * submit specialist profile for approval
     */
    submitSpecialistProfileApprovalRequest(state) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },

    submitSpecialistProfileApprovalSuccess(state, action: PayloadAction<UpdateSpecialistProfileSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      // Update profile_status in authData
      if (state.authData) {
        (state.authData as any).profile_status = action.payload.data.profile_status;
      }
    },

    submitSpecialistProfileApprovalFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    patientAccountExportRequest(state, action: PayloadAction<PatientSettingExportRequestPayload>) {
      state.isAccountExportLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    }, //export loading state
    patientAccountExportSuccess(state, action: PayloadAction<PatientSettingExportSuccessResponse>) {
      state.isAccountExportLoading = false;
      state.authSuccess = action.payload.status_code;
      state.accountExportStatus = action.payload.data.account_export_status;
    }, //api success response
    patientAccountExportFailure(state, action: PayloadAction<APIResponse>) {
      state.isAccountExportLoading = false;
      state.authFailure = action.payload.status_code;
    }, //api failure response

    patientAccountDeletion(state, action: PayloadAction<PatientAccountDeletionRequestPayload>) {
      state.isAccountDeletionLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    }, //export loading state
    patientAccountDeletionSuccess(state, action: PayloadAction<PatientAccountDeletionSuccessResponse>) {
      state.isAccountDeletionLoading = false;
      state.authSuccess = action.payload.status_code;
      state.accountDeletionStatus = action.payload.data.account_ticket_status;
    }, //api success response
    patientAccountDeletionFailure(state, action: PayloadAction<APIResponse>) {
      state.isAccountDeletionLoading = false;
      state.authFailure = action.payload.status_code;
    },

    patientAccountCancelation(state) {
      state.isAccountDeletionLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    }, //export loading state
    patientAccountCancelationSuccess(state, action: PayloadAction<PatientAccountDeletionSuccessResponse>) {
      state.isAccountDeletionLoading = false;
      state.authSuccess = action.payload.status_code;
      state.accountDeletionStatus = action.payload.data.account_ticket_status;

      // reset export status
      state.accountExportStatus = PatientAccountTicketRequestStatus.NotApplicable;
    }, //api success response
    patientAccountCancelationFailure(state, action: PayloadAction<APIResponse>) {
      state.isAccountDeletionLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * validate specialist invitation
     */
    validateSpecialistInvitationRequest(state, action: PayloadAction<ValidateSpecialistInvitationRequestPayload>) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    validateSpecialistInvitationSuccess(state, action: PayloadAction<ValidateSpecialistInvitationSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;

      // pre-fill the auth session form data
      const responseData = action.payload.data;

      if (!state.authSession.formData) {
        state.authSession.formData = {};
      }

      state.authSession.email = decryptInline(responseData.email);
      state.authSession.phone = decryptInline(responseData.phone);
      state.authSession.originalPhone = decryptInline(responseData.phone);
      state.authSession.formData.name = formatName(decryptInline(responseData.name));
      state.authSession.formData.specialization = decryptInline(responseData.specialization);
      state.authSession.formData.location = decryptInline(responseData.location);
    },

    validateSpecialistInvitationFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    /**
     * utility reducers
     */
    resetAuthMessage(state) {
      state.authFailure = false;
      state.authSuccess = false;
    },

    cancelAuthRequest(state) {
      state.isLoading = false;
      state.isAccountDeletionLoading = false;
      state.isAccountExportLoading = false;
      state.authSession.formData = {};
      state.profileupdateLoading = false;
    },

    // ==============================
    // logout
    // ==============================
    logoutRequest(state) {
      state.isLoading = true;
      state.authSuccess = false;
      state.authFailure = false;
    },
    logoutSuccess(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      // if logout successfully
      if (action.payload.status_code === 'S-10121') {
        state.authData = null;
        state.isAuthenticated = false;
      }
    },
    logoutFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },

    resetAuth(state) {
      state.authData = null;
      state.isAuthenticated = false;
    },

    /**
     * specialist signup form persistence
     */

    resetAuthFormData(state) {
      state.otp_attempts = 0;
      state.otpTimer = '';
      state.authSession.formData = {};
    },

    /**
     * persist reducers
     */
    resetPersistData(state) {
      state.persist = initialState.persist;
    },

    updateAccountExportStatus(state, action: PayloadAction<PatientAccountTicketRequestStatus>) {
      state.accountExportStatus = action.payload;
    },
    updateAccountDeletionStatus(state, action: PayloadAction<PatientAccountTicketRequestStatus>) {
      state.accountDeletionStatus = action.payload;
    },
    updateSpecialistProfileStatus(state, action: PayloadAction<SpecialistProfileStatus>) {
      if (state.authData) {
        (state.authData as any).profile_status = action.payload;
      }
    },
    /**
     * auth session reducers
     */
    setAuthSession(state, action: PayloadAction<AuthSliceInitalState['authSession']>) {
      state.authSession = {...action.payload, disabled: false};
    },
    resetAuthSession(state) {
      state.authSession = {
        auth_type: null,
        email: '',
        originalPhone: '',
        phone: '',
        disabled: false,
        formData: {},
      };
    },

    /**
     * account ticket status reducers
     */
    getAccountTicketStatusRequest(state) {
      state.isLoading = true;
      state.authFailure = false;
      state.authSuccess = false;
    },
    getAccountTicketStatusSuccess(state, action: PayloadAction<GetAccountTicketStatusSuccessResponse>) {
      state.isLoading = false;
      state.authSuccess = action.payload.status_code;
      state.accountDeletionStatus = action.payload.data.account_delete_status;
      state.accountExportStatus = action.payload.data.account_export_status;
    },
    getAccountTicketStatusFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.authFailure = action.payload.status_code;
    },
  },
});

export default authSlice.reducer;
export const {
  // login otp request
  loginOtpFailure,
  loginOtpRequest,
  loginOtpSuccess,

  // signup otp request
  signupOtpFailure,
  signupOtpRequest,
  signupOtpSuccess,

  // specialist signup request
  specialistSignupOtpFailure,
  specialistSignupOtpRequest,
  specialistSignupOtpSuccess,

  // verify licence
  verifyLicenceFailure,
  verifyLicenceRequest,
  verifyLicenceSuccess,

  // login otp veriry
  loginOTPVerifyFailure,
  loginOTPVerifyRequest,
  loginOTPVerifySuccess,

  // signup otp verify
  singupOTPVerifyFailure,
  singupOTPVerifyRequest,
  singupOTPVerifySuccess,

  // login otp resend
  loginOtpResendFailure,
  loginOtpResendRequest,
  loginOtpResendSuccess,

  // signup otp resend
  signupOtpResendFailure,
  signupOtpResendRequest,
  signupOtpResendSuccess,

  // login mfa verify
  loginMFAVerifyFailure,
  loginMFAVerifyRequest,
  loginMFAVerifySuccess,

  // signup mfa verify
  signupMFAVerifyFailure,
  signupMFAVerifyRequest,
  signupMFAVerifySuccess,

  // login mfa resend
  loginMFAResendRequest,
  loginMFAResendFailure,
  loginMFAResendSuccess,

  // signup mfa resend
  signupMFAResendFailure,
  signupMFAResendRequest,
  signupMFAResendSuccess,

  // get profile
  getProfileFailure,
  getProfileRequest,
  getProfileSuccess,

  // edit profile
  editProfileFailure,
  editProfileRequest,
  editProfileSuccess,

  // profile otp request
  profileOtpRequestRequest,
  profileOtpRequestFailure,
  profileOtpRequestSuccess,

  // profile otp resend
  profileOtpResendRequest,
  profileOtpResendFailure,
  profileOtpResendSuccess,

  // profile otp verify
  profileOtpVerifyRequest,
  profileOtpVerifyFailure,
  profileOtpVerifySuccess,

  // get specialist profile
  getSpecialistProfileRequest,
  getSpecialistProfileSuccess,
  getSpecialistProfileFailure,

  // update specialist profile
  updateSpecialistProfileRequest,
  updateSpecialistProfileSuccess,
  updateSpecialistProfileFailure,

  // validate specialist invitation
  validateSpecialistInvitationRequest,
  validateSpecialistInvitationSuccess,
  validateSpecialistInvitationFailure,

  // submit specialist profile approval
  submitSpecialistProfileApprovalRequest,
  submitSpecialistProfileApprovalSuccess,
  submitSpecialistProfileApprovalFailure,

  // cancel and reset reducers
  resetAuthMessage,
  cancelAuthRequest,

  //Patient setting export
  patientAccountExportRequest,
  patientAccountExportSuccess,
  patientAccountExportFailure,

  //Patient setting deletion
  patientAccountDeletion,
  patientAccountDeletionSuccess,
  patientAccountDeletionFailure,

  //patient account cancel deletion
  patientAccountCancelation,
  patientAccountCancelationSuccess,
  patientAccountCancelationFailure,

  //account ticket status
  getAccountTicketStatusRequest,
  getAccountTicketStatusSuccess,
  getAccountTicketStatusFailure,

  // logout
  logoutFailure,
  logoutRequest,
  logoutSuccess,

  resetAuth,

  // utility reducers
  resetAuthFormData,
  resetPersistData,
  updateAccountExportStatus,
  updateAccountDeletionStatus,
  updateSpecialistProfileStatus,
  setAuthSession,
  resetAuthSession,
} = authSlice.actions;
